// Static changelog feed consumed by the global ReleaseNotesModal.
//
// Entries MUST be ordered newest-first. The first entry is treated as the
// "current release" and rendered prominently when the user has not yet
// dismissed it; older entries collapse into expandable disclosures.
//
// Each entry shape:
//   {
//     version: "0.3.0",          // matches package.json (or a sub-build tag)
//     date:    "2026-04-25",     // ISO date string
//     title:   "Safe-deploy",    // optional short headline
//     highlights: ["..."],       // optional 1-3 bullet hero summary
//     items:   ["..."]           // detailed bullet list
//   }
//
// Adding a release: prepend a new object. Bump `package.json.version` to
// match the new entry's `version` so the existing `__APP_VERSION__` build
// constant + the changelog stay in lockstep.

export const CHANGELOG = [
  {
    version: "0.5.0",
    date: "2026-05-11",
    title: "Exportable IP table + minor improvements",
    highlights: [
      "Fabric Topology: a new \u201CExport IP table (CSV)\u201D button downloads the current topology\u2019s IP assignments as a CSV for audits or documentation.",
      "A new \u201CShutdown unused interfaces\u201D toggle tells the generator to emit `shutdown` on every unconfigured physical port at deploy time, reducing the attack surface on unused ports (loopbacks are never affected)."
    ],
    items: [
      "Management: SNMP version selector now includes v3. When v3 is selected, you can configure the username, security level (noauth / auth / priv), and whether to use informs. Dedicated cards let you add SNMPv3 Views, Groups, and Users with full field support.",
      "Terminal: the SSH terminal input handling was rebuilt around a new `LineEditor` component, making history navigation, pasting, and prompt rendering more reliable.",
      "The Inventory \u201CDrifted\u201D chip no longer flips on devices that didn\u2019t actually change. The connector now canonicalises a handful of equivalent forms in `show running-config` before hashing, so two snapshots of an untouched device produce the same hash.",
      "Connector: the gateway now persists uptime-history records for both \u201Cup\u201D and \u201Cdown\u201D reachability states so the Fabric uptime timeline is complete even across flaps. Invalid uptime values are safely coerced to 0 instead of causing errors."
    ]
  },
  {
    version: "0.4.0",
    date: "2026-04-27",
    title: "Faster compliance + zero-device projects",
    highlights: [
      "Inventory now flips to Compliant within seconds of a successful deploy \u2014 the connector recollects running-config immediately instead of waiting for the next poll.",
      "Routers and switches can be removed entirely. Switch-only or router-only projects show a tailored empty state instead of a forced default device."
    ],
    items: [
      "Connector: after every successful merge/commands deploy, the just-deployed device's per-device cache is invalidated and a fresh telemetry snapshot is ingested in the same loop tick. No more 1\u20135 minute window where the inventory shows Drifted on a freshly-deployed device.",
      "Connector: capability probe (`show version`) no longer caches NONE results for 24 hours. A single transient probe failure used to permanently refuse safe deploys until the TTL expired; now the probe re-runs every deploy and surfaces the underlying reason (`show-version-unrecognized`, SSH error, etc.) in the log.",
      "Editor: tab context-menu \u201cRemove\u201d now works on the last device. The page renders a \u201cCreate your first router/switch\u201d empty-state card when the project has zero of that device type. Brand-new projects still start with one of each by default.",
      "Linter: VLAN SVIs sharing the same VLAN ID across switches no longer flag as `subnet-overlap` (a VLAN is a single L2 broadcast domain by definition, so the same /24 is required, not a bug). A new `svi-duplicate-ip` error fires only when two SVIs in the same VLAN actually share the same host IP. Cross-VLAN overlaps and SVI-vs-routed-interface overlaps still warn.",
      "Deployment detail dialog: SHA-256 hashes in the Hash before / Hash after fields no longer overlap or run past their column. They\u2019re now full-width rows in a monospace font with proper word breaks."
    ]
  },
  {
    version: "0.3.0",
    date: "2026-04-25",
    title: "Safe deploy + connector page polish",
    highlights: [
      "Deploys are now wrapped in a 1-minute auto-rollback. If the connector loses access mid-push, the device reverts itself.",
      "First-deploy acknowledgement is gone for new connectors — the safety net handles that case automatically."
    ],
    items: [
      "Generator no longer emits destructive `no ip address` before re-applying the same address (no more SSH flap on management SVIs).",
      "Connector now diffs the live running-config and only pushes the minimal command set — most re-deploys become no-ops.",
      "Fresh-session reconnect verification: a deploy is only confirmed after the connector successfully opens a brand-new SSH session.",
      "Block-aware planner flags risky changes (line vty, AAA, ACLs, default route) so the wrapper can fail closed if rollback is unavailable.",
      "Device-level deploy concurrency guard: only one queued/running merge job per device.",
      "Inventory compliance tag is now a real compliance check (Compliant / Drifted / Not deployed / Unmanaged) based on a hash comparison between the running-config and the last successful deploy — not just \u2018is this device modelled in NetBase?\u2019.",
      "Connector page: search + assignment progress, device-type icons, distinct chip styling for assigned/unassigned/all-connectors, and a clearer expanded-row treatment that dims siblings."
    ]
  },
  {
    version: "0.2.6",
    date: "2026-04-19",
    title: "Earlier 0.2.x releases",
    items: [
      "Initial connector-driven deploy flow with pre-deploy diff modal.",
      "Per-device running-config telemetry uploaded by the connector.",
      "Project-aware editor presence + section-level sync.",
      "Cisco SSH provider with NETCONF / RESTCONF interface discovery."
    ]
  }
];

// The "current" release is whatever sits at the head of the list. The gate
// uses this to compare against the user's last-seen version.
export const LATEST_RELEASE = CHANGELOG[0];

// Storage key the gate writes to once the user dismisses the modal. Kept
// distinct from the legacy `version` storage-schema marker.
export const LAST_SEEN_RELEASE_STORAGE_KEY = "lastSeenReleaseVersion";
